import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityListViewModel } from 'src/components/activityManagement/activityViewModel';
import { mapActivitiesToViewModels } from 'src/components/activityManagement/activityViewModelMapper';
import AppContext from '../AppContext';
import { ListActivitiesByOrgIdPayload } from './generated/types';

export const useListAllActivitiesInitiatedByGmg = (cursor: string | null) => {
    const context = useContext(AppContext);
    const { t } = useTranslation();

    const query = `query ListAllActivitiesInitiatedByGmg($nextCursor: String) {
        listAllActivitiesInitiatedByGmg(nextCursor: $nextCursor) {
            nextCursor
            activities {
                activityId
                timestamp
                activityType
                activityDetails {
                    __typename
                    ... on ActivityChangedAttributes {
                    __typename
                        attributes {
                            name
                            newValue
                            oldValue
                        }
                    }
                    ... on ActivityChangedLicenseSystemAccess {
                    __typename
                        licenseSystemIds
                        moduleId
                        role
                    }
                    ... on ActivityChangedModuleAccess {
                    __typename
                        moduleIds
                    }
                    ... on ActivityDownloadedLicenseSystemUpdate {
                    __typename
                        updateNumber
                    }
                    ... on ActivityChangedSiteAccess {
                    __typename
                        sites {
                            orgId
                            orgName
                        }
                        headOrg {
                            orgId
                            orgName
                        }
                    }
                    ... on ActivityClosedSessions {
                    __typename
                        appNames
                    }
                }
                actingUser {
                    email
                    name
                    orgId
                    orgName
                    userId
                }
                targetResource {
                    orgId
                    orgName
                    resourceType
                    userEmail
                    userId
                    userName
                    licenseSystemId
                }
            }
        }
    }`;

    return useQuery<ActivityListViewModel, Error>({
        queryKey: ['listAllActivitiesInitiatedByGmg', cursor || null, context.language],
        queryFn: async () => {
            const variables = {
                nextCursor: cursor,
            };
            const response = await (context.api.graphql({ query, variables }) as Promise<{
                data: {
                    listAllActivitiesInitiatedByGmg: ListActivitiesByOrgIdPayload;
                };
            }>);
            const { nextCursor, activities } = response.data.listAllActivitiesInitiatedByGmg;
            const mappedActivities = mapActivitiesToViewModels((activities || []), t);
            return { nextCursor: nextCursor || null, activities: mappedActivities };
        },
        enabled: true,
        refetchOnWindowFocus: false,
        // switch off caching for this query as ANY mutation triggered by a GMG user will make the data stale.
        gcTime: 0,
        staleTime: 0,
    });
};
